import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Elements, CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import { Field, Select, TextArea, PillToggle } from "@/components/Form";
import ProgressBar from "@/components/ProgressBar";
import { Spinner } from "@/components/Loader";
import { useAuth } from "@/lib/auth";
import { useAssessment } from "@/lib/assessment";
import { apiGet, apiPost } from "@/lib/api";
import { getStripe } from "@/lib/stripe";
import { calcBmi, fmtMoney, ftInToCm, lbsToKg, cmToFtIn, kgToLbs } from "@/lib/format";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

const TOTAL_STEPS = 5;

const HEALTH_GOALS = ["Lose weight", "Build muscle", "Better sleep", "More energy", "Sharper focus", "Smoother skin", "Heal faster", "Live longer"];
const MEDICAL_CONDITIONS = ["None", "Hypertension", "Diabetes (Type 2)", "Thyroid", "PCOS", "Heart disease", "Cancer history", "Pregnancy"];

export default function Assessment() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { consumer } = useAuth();
  const { data, update, updateQ, updateAddr } = useAssessment();
  const [program, setProgram] = useState(null);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!data.programSlug || data.programSlug !== slug) {
      navigate(`/app/programs/${slug}`, { replace: true });
      return;
    }
    apiGet(`/consumer/programs/${slug}`, { auth: false }).then(setProgram).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Auto-calc BMI
  useEffect(() => {
    const bmi = calcBmi(data.questionnaire.heightCm, data.questionnaire.weightKg);
    if (bmi && bmi !== data.questionnaire.bmi) updateQ({ bmi });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.questionnaire.heightCm, data.questionnaire.weightKg]);

  // Pre-fill shipping from profile
  useEffect(() => {
    if (consumer?.shippingAddress && !data.shippingAddress.line1) {
      updateAddr(consumer.shippingAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumer]);

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const consultFee = program?.consultFee || 49;

  return (
    <div data-testid="assessment-page" style={{ minHeight: "100vh", background: C.cream, paddingBottom: 64 }}>
      <header style={{ background: C.ink, color: C.cream, padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to={`/app/programs/${slug}`} data-testid="assessment-back" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: "rgba(250,246,240,0.65)", textDecoration: "none" }}>← BACK</Link>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.peach }}>★ ASSESSMENT</span>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "rgba(250,246,240,0.55)" }}>STEP {step} / {TOTAL_STEPS}</span>
      </header>
      <div style={{ background: C.ink, paddingBottom: 24 }}>
        <ProgressBar current={step} total={TOTAL_STEPS} />
      </div>

      <div style={{ maxWidth: 600, margin: "0 auto", padding: "32px 24px" }}>
        {step === 1 && <BasicsStep data={data.questionnaire} updateQ={updateQ} onNext={next} />}
        {step === 2 && <MedicalStep data={data.questionnaire} updateQ={updateQ} onNext={next} onBack={back} />}
        {step === 3 && <GoalsStep data={data.questionnaire} updateQ={updateQ} onNext={next} onBack={back} />}
        {step === 4 && <ShippingStep addr={data.shippingAddress} updateAddr={updateAddr} onNext={next} onBack={back} />}
        {step === 5 && <PaymentStep program={program} fee={consultFee} onBack={back} />}
      </div>
    </div>
  );
}

// ---------- STEP 1: Basics ----------
function BasicsStep({ data, updateQ, onNext }) {
  // Imperial-display state derived from stored metric values, so a returning
  // user sees their saved height/weight as ft/in/lbs even though we persist
  // cm/kg under the hood for the backend.
  const initialFtIn = cmToFtIn(data.heightCm);
  const initialLbs = kgToLbs(data.weightKg);
  const [heightFt, setHeightFt] = useState(initialFtIn.ft || "");
  const [heightIn, setHeightIn] = useState(initialFtIn.in || "");
  const [weightLbs, setWeightLbs] = useState(initialLbs || "");

  // Sync derived metric values into the assessment store whenever the user
  // edits any imperial input. Stores "" when blank so the parent's
  // valid check still gates Continue correctly.
  const onHeightFt = (v) => {
    setHeightFt(v);
    updateQ({ heightCm: ftInToCm(v, heightIn) });
  };
  const onHeightIn = (v) => {
    setHeightIn(v);
    updateQ({ heightCm: ftInToCm(heightFt, v) });
  };
  const onWeightLbs = (v) => {
    setWeightLbs(v);
    updateQ({ weightKg: lbsToKg(v) });
  };

  const valid = data.sex && data.dateOfBirth && data.heightCm && data.weightKg;
  return (
    <div data-testid="assessment-step-basics">
      <Eyebrow>★ STEP 01 / 05</Eyebrow>
      <Headline before="QUICK" italic="basics." size={56} className="mt-4" />
      <Select label="BIOLOGICAL SEX" value={data.sex} onChange={(v) => updateQ({ sex: v })} options={["Male", "Female", "Intersex", "Prefer not to say"]} testId="basics-sex" />

      {/* DOB — wrapping div constrains the iOS native date picker which
          otherwise stretches beyond the parent on Mobile Safari. */}
      <div style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        <Field label="DATE OF BIRTH" type="date" value={data.dateOfBirth} onChange={(v) => updateQ({ dateOfBirth: v })} testId="basics-dob" />
      </div>

      {/* Height — feet + inches (US default). 3-column grid: ft / in / lbs */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.4fr", gap: 10 }}>
        <Field
          label="HEIGHT (FT)"
          type="number"
          value={heightFt}
          onChange={onHeightFt}
          placeholder="5"
          testId="basics-height-ft"
          inputMode="numeric"
        />
        <Field
          label="(IN)"
          type="number"
          value={heightIn}
          onChange={onHeightIn}
          placeholder="10"
          testId="basics-height-in"
          inputMode="numeric"
        />
        <Field
          label="WEIGHT (LBS)"
          type="number"
          value={weightLbs}
          onChange={onWeightLbs}
          placeholder="180"
          testId="basics-weight-lbs"
          inputMode="numeric"
        />
      </div>

      {data.bmi && (
        <div data-testid="basics-bmi" style={{ marginTop: 14, paddingLeft: 12, borderLeft: `2px solid ${C.peachDeep}` }}>
          <div style={{ fontSize: 13, color: C.ink }}>
            BMI <strong style={{ letterSpacing: "-0.01em" }}>{data.bmi}</strong> <span style={{ color: C.warmGray, marginLeft: 6 }}>· noted.</span>
          </div>
          <div style={{ marginTop: 4, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 14, color: C.warmGray, lineHeight: 1.4 }}>
            The rest of your story matters more.
          </div>
        </div>
      )}
      <div style={{ marginTop: 32 }}>
        <Button variant="primary" size="lg" block testId="basics-next-btn" disabled={!valid} onClick={onNext}>CONTINUE →</Button>
      </div>
    </div>
  );
}

// ---------- STEP 2: Medical ----------
function MedicalStep({ data, updateQ, onNext, onBack }) {
  const toggle = (cond) => {
    const cur = data.medicalConditions || [];
    const next = cur.includes(cond) ? cur.filter((c) => c !== cond) : [...cur, cond];
    updateQ({ medicalConditions: next });
  };
  return (
    <div data-testid="assessment-step-medical">
      <Eyebrow>★ STEP 02 / 05</Eyebrow>
      <Headline before="MEDICAL" italic="history." size={56} className="mt-4" />
      <p style={{ marginTop: 18, fontSize: 14, color: C.warmGray, lineHeight: 1.55 }}>Tap all that apply. No wrong answers.</p>
      <div style={{ marginTop: 24, display: "flex", flexWrap: "wrap", gap: 10 }}>
        {MEDICAL_CONDITIONS.map((c) => (
          <PillToggle key={c} label={c} active={(data.medicalConditions || []).includes(c)} onClick={() => toggle(c)} testId={`medical-${c.toLowerCase().replace(/[^a-z0-9]/g, "-")}`} />
        ))}
      </div>
      <Field label="CURRENT MEDICATIONS" value={data.currentMedications} onChange={(v) => updateQ({ currentMedications: v })} placeholder="List any prescription or OTC meds" testId="medical-meds" />
      <Field label="ALLERGIES" value={data.allergies} onChange={(v) => updateQ({ allergies: v })} placeholder="None / list known allergies" testId="medical-allergies" />
      <TextArea label="PREVIOUS EXPERIENCE WITH SIMILAR PROGRAMS" value={data.previousExperience} onChange={(v) => updateQ({ previousExperience: v })} testId="medical-prev" rows={3} />
      <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
        <Button variant="ghost" testId="medical-back" onClick={onBack}>← BACK</Button>
        <Button variant="primary" testId="medical-next" onClick={onNext} block>CONTINUE →</Button>
      </div>
    </div>
  );
}

// ---------- STEP 3: Goals ----------
function GoalsStep({ data, updateQ, onNext, onBack }) {
  const toggle = (g) => {
    const cur = data.healthGoals || [];
    const next = cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g];
    updateQ({ healthGoals: next });
  };
  const valid = (data.healthGoals || []).length > 0;
  return (
    <div data-testid="assessment-step-goals">
      <Eyebrow>★ STEP 03 / 05</Eyebrow>
      <Headline before="YOUR" italic="goals." size={56} className="mt-4" />
      <p style={{ marginTop: 18, fontSize: 14, color: C.warmGray }}>Pick anything that resonates. Mix and match.</p>
      <div style={{ marginTop: 24, display: "flex", flexWrap: "wrap", gap: 10 }}>
        {HEALTH_GOALS.map((g) => (
          <PillToggle key={g} label={g} active={(data.healthGoals || []).includes(g)} onClick={() => toggle(g)} testId={`goal-${g.toLowerCase().replace(/[^a-z0-9]/g, "-")}`} />
        ))}
      </div>
      <TextArea label="ANYTHING ELSE YOU'D LIKE THE PHYSICIAN TO KNOW?" value={data.additionalNotes} onChange={(v) => updateQ({ additionalNotes: v })} testId="goals-notes" rows={4} />
      <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
        <Button variant="ghost" testId="goals-back" onClick={onBack}>← BACK</Button>
        <Button variant="primary" testId="goals-next" onClick={onNext} block disabled={!valid}>CONTINUE →</Button>
      </div>
    </div>
  );
}

// ---------- STEP 4: Shipping ----------
function ShippingStep({ addr, updateAddr, onNext, onBack }) {
  const valid = addr.line1 && addr.city && addr.state && /^\d{5}$/.test(addr.zip);
  return (
    <div data-testid="assessment-step-shipping">
      <Eyebrow>★ STEP 04 / 05</Eyebrow>
      <Headline before="WHERE TO" italic="ship?" size={56} className="mt-4" />
      <Field label="ADDRESS LINE 1" value={addr.line1} onChange={(v) => updateAddr({ line1: v })} placeholder="123 Main St" testId="ship-line1" />
      <Field label="ADDRESS LINE 2" value={addr.line2} onChange={(v) => updateAddr({ line2: v })} placeholder="Apt / Suite (optional)" testId="ship-line2" />
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 14 }}>
        <Field label="CITY" value={addr.city} onChange={(v) => updateAddr({ city: v })} testId="ship-city" />
        <Field label="STATE" value={addr.state} onChange={(v) => updateAddr({ state: v.toUpperCase() })} placeholder="CA" testId="ship-state" />
        <Field label="ZIP" value={addr.zip} onChange={(v) => updateAddr({ zip: v })} testId="ship-zip" />
      </div>
      <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
        <Button variant="ghost" testId="ship-back" onClick={onBack}>← BACK</Button>
        <Button variant="primary" testId="ship-next" onClick={onNext} block disabled={!valid}>CONTINUE →</Button>
      </div>
    </div>
  );
}

// ---------- STEP 5: Payment ----------
function PaymentStep({ program, fee, onBack }) {
  const [stripePromise, setStripePromise] = useState(null);
  useEffect(() => {
    getStripe().then(setStripePromise).catch(() => setStripePromise(null));
  }, []);
  if (!stripePromise) {
    return (
      <div data-testid="assessment-step-payment-loading" style={{ padding: 40, textAlign: "center" }}><Spinner /></div>
    );
  }
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm program={program} fee={fee} onBack={onBack} />
    </Elements>
  );
}

function PaymentForm({ program, fee, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { consumer } = useAuth();
  const { data, reset } = useAssessment();
  const [submitting, setSubmitting] = useState(false);

  const cardOptions = useMemo(() => ({
    style: {
      base: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: "15px",
        color: "#0F0E0D",
        "::placeholder": { color: "#A8A29E" },
      },
      invalid: { color: "#D85A3A" },
    },
  }), []);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    try {
      // 1. Create PaymentIntent for consult fee
      const intent = await apiPost("/consumer/stripe/payment-intent", {
        amount: Number(fee),
        currency: "usd",
        purpose: "consult_fee",
      });
      // 2. Confirm card payment with Stripe (saves PM to customer for future use)
      const result = await stripe.confirmCardPayment(intent.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: `${consumer?.firstName || ""} ${consumer?.lastName || ""}`.trim() || undefined,
            email: consumer?.email,
          },
        },
        setup_future_usage: "off_session",
      });
      if (result.error) throw new Error(result.error.message || "Payment failed");
      if (result.paymentIntent?.status !== "succeeded") {
        throw new Error("Payment not completed");
      }
      // 3. Submit consultation. Phase B clean-cut — BE no longer
      //    accepts selectedProductId / selectedProtocolTier. Payload
      //    must carry the canonical compound + stage selection. Server
      //    resolves the linked product + monthly price from the program.
      const consult = await apiPost("/consumer/consultations", {
        programSlug: data.programSlug,
        selectedCompoundId: data.selectedCompoundId,
        selectedStage: data.selectedStage,
        questionnaire: data.questionnaire,
        shippingAddress: data.shippingAddress,
        stripePaymentIntentId: result.paymentIntent.id,
        monthlyPrice: data.monthlyPrice,
      });
      reset();
      navigate(`/app/done?cid=${consult.id}`);
    } catch (err) {
      toast(err.detail || err.message || "Payment failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="assessment-payment-form">
      <Eyebrow>★ STEP 05 / 05</Eyebrow>
      <Headline before="ALMOST" italic="there." size={56} className="mt-4" />

      <div style={{ marginTop: 28, padding: 24, background: C.bone }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>★ YOUR SELECTION</div>
        <div style={{ marginTop: 12, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: C.ink }} data-testid="assessment-selection-headline">
          {(() => {
            // Headline reads compound + stage when available. The stored
            // selection is canonical Phase B (compound + stage); we fall
            // back to legacy fields only for in-flight sessions started
            // before this deploy.
            const programName = (program?.name || data.programSlug?.replace(/-/g, " ") || "").toUpperCase();
            const compound = data.selectedCompoundName || data.selectedCompoundSlug?.replace(/-/g, " ");
            const stage = data.selectedStageLabel || (typeof data.selectedStage === "string" ? data.selectedStage : null);
            if (compound) {
              const isSameLabel = stage && stage.toLowerCase() === compound.toLowerCase();
              return stage && !isSameLabel
                ? `${programName} — ${compound.toUpperCase()} · ${stage.toUpperCase()}`
                : `${programName} — ${compound.toUpperCase()}`;
            }
            // Legacy fallback for older sessions.
            return `${programName} — ${(data.selectedProtocolTier || "").toUpperCase()}`;
          })()}
        </div>
        <div style={{ marginTop: 4, fontSize: 13, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
          {fmtMoney(data.monthlyPrice || 0)}/mo — charged only after physician approval.
        </div>

        {(() => {
          const visible = ((data.selectedProtocol && data.selectedProtocol.bundleItems) || []).filter((b) => b && b.isVisible !== false);
          if (visible.length === 0) return null;
          return (
            <>
              <div style={{ marginTop: 18, fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>
                MONTHLY DELIVERY INCLUDES
              </div>
              <ul data-testid="review-bundle" style={{ margin: "10px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
                {visible.map((it, i) => (
                  <li key={i} style={{ fontSize: 13, color: C.ink }}>
                    • {it.displayName}
                    {it.quantity > 1 && <span style={{ color: C.warmGray, marginLeft: 4 }}>({it.quantity})</span>}
                  </li>
                ))}
              </ul>
            </>
          );
        })()}

        {data.selectedProtocol?.dosing?.frequency && (
          <div style={{ marginTop: 16, fontSize: 12, color: C.warmGray, lineHeight: 1.5 }} data-testid="review-dosing">
            <strong style={{ color: C.ink, letterSpacing: "0.1em", fontSize: 11 }}>DOSING </strong>
            {data.selectedProtocol.dosing.frequency}
            {data.selectedProtocol.dosing.supplyDuration ? ` · ${data.selectedProtocol.dosing.supplyDuration}` : ""}
          </div>
        )}

        <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.warmGray3}`, display: "flex", justifyContent: "space-between", fontSize: 14, color: C.ink }}>
          <span>Consultation fee (today)</span>
          <span style={{ fontWeight: 800 }}>{fmtMoney(fee)}</span>
        </div>
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.warmGray3}`, display: "flex", justifyContent: "space-between", fontSize: 18, fontWeight: 800 }}>
          <span>Today's total</span>
          <span>{fmtMoney(fee)}</span>
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 8 }}>CARD DETAILS</div>
        <div className="StripeElement" data-testid="stripe-card-element">
          <CardElement options={cardOptions} />
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: C.warmGray, letterSpacing: "0.05em" }}>Charges show as <strong>VOYA9.HEALTH</strong> on your statement.</div>
      </div>

      <div style={{ marginTop: 32, display: "flex", gap: 12 }}>
        <Button variant="ghost" testId="payment-back" onClick={onBack} disabled={submitting}>← BACK</Button>
        <Button variant="primary" testId="payment-pay-btn" type="submit" block disabled={!stripe || submitting}>
          {submitting ? "PROCESSING…" : `PAY ${fmtMoney(fee)} →`}
        </Button>
      </div>
    </form>
  );
}
